import { useState, useMemo } from 'react';

interface AppointmentPickerProps {
  selectedDate: string;
  selectedTime: string;
  onDateChange: (date: string) => void;
  onTimeChange: (time: string) => void;
}

const TIME_SLOTS = [
  { label: '10:00', value: '10:00' },
  { label: '11:00', value: '11:00' },
  { label: '13:00', value: '13:00' },
  { label: '14:00', value: '14:00' },
  { label: '15:00', value: '15:00' },
  { label: '16:00', value: '16:00' },
  { label: '17:00', value: '17:00' },
];

const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

function getAvailableSlots(dateStr: string): string[] {
  if (!dateStr) return [];
  const day = new Date(dateStr).getDay();
  if (day === 0) return []; // Sunday closed
  if (day === 6) return TIME_SLOTS.slice(0, 5).map((s) => s.value); // Sat 10–16
  return TIME_SLOTS.map((s) => s.value);
}

export default function AppointmentPicker({
  selectedDate,
  selectedTime,
  onDateChange,
  onTimeChange,
}: AppointmentPickerProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells: (number | null)[] = Array(firstDay).fill(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewYear, viewMonth]);

  const isDisabled = (day: number) => {
    const d = new Date(viewYear, viewMonth, day);
    d.setHours(0, 0, 0, 0);
    const dayOfWeek = d.getDay();
    return d < today || dayOfWeek === 0;
  };

  const isSelected = (day: number) => {
    return selectedDate === `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const isToday = (day: number) => {
    return (
      today.getFullYear() === viewYear &&
      today.getMonth() === viewMonth &&
      today.getDate() === day
    );
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const handleSelectDay = (day: number) => {
    if (isDisabled(day)) return;
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onDateChange(dateStr);
    onTimeChange('');
  };

  const availableSlots = getAvailableSlots(selectedDate);

  const isPrevDisabled =
    viewYear === today.getFullYear() && viewMonth === today.getMonth();

  const formatSelectedDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const dayName = WEEKDAY_LABELS[d.getDay()];
    return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日（週${dayName}）`;
  };

  return (
    <div className="space-y-5">
      {/* Calendar */}
      <div className="bg-white border border-[#e2ddd8] rounded-xl overflow-hidden">
        {/* Month nav */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#e2ddd8]">
          <button
            type="button"
            onClick={handlePrevMonth}
            disabled={isPrevDisabled}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f4f2ef] disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-[#5a5a5a]"></i>
          </button>
          <span className="text-sm font-semibold text-[#1a1a1a]">
            {viewYear} 年 {viewMonth + 1} 月
          </span>
          <button
            type="button"
            onClick={handleNextMonth}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f4f2ef] transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-[#5a5a5a]"></i>
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 border-b border-[#e2ddd8]">
          {WEEKDAY_LABELS.map((w, i) => (
            <div
              key={w}
              className={`py-2.5 text-center text-xs font-medium ${
                i === 0 ? 'text-red-400' : 'text-[#8a8a8a]'
              }`}
            >
              {w}
            </div>
          ))}
        </div>

        {/* Days */}
        <div className="grid grid-cols-7 p-2 gap-1">
          {calendarDays.map((day, idx) => {
            if (day === null) return <div key={`empty-${idx}`} />;
            const disabled = isDisabled(day);
            const selected = isSelected(day);
            const todayMark = isToday(day);
            const isSunday = new Date(viewYear, viewMonth, day).getDay() === 0;

            return (
              <button
                key={day}
                type="button"
                onClick={() => handleSelectDay(day)}
                disabled={disabled}
                className={`
                  h-9 w-full flex items-center justify-center rounded-lg text-sm font-medium transition-all cursor-pointer
                  ${selected
                    ? 'bg-[#b8956a] text-white'
                    : disabled
                    ? 'text-[#c0bbb5] cursor-not-allowed'
                    : isSunday
                    ? 'text-red-300 cursor-not-allowed'
                    : 'text-[#1a1a1a] hover:bg-[#f4f2ef]'
                  }
                  ${todayMark && !selected ? 'ring-1 ring-[#b8956a] ring-inset' : ''}
                `}
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="px-4 pb-3 flex items-center gap-4 text-xs text-[#8a8a8a]">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full border border-[#b8956a] inline-block"></span>
            今天
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-[#b8956a] inline-block"></span>
            已選
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-[#f0ede9] inline-block"></span>
            不可選（休息）
          </span>
        </div>
      </div>

      {/* Time Slots */}
      {selectedDate && (
        <div>
          <p className="text-sm font-medium text-[#1a1a1a] mb-3">
            <span className="text-[#b8956a]">{formatSelectedDate(selectedDate)}</span> 可預約時段
          </p>
          {availableSlots.length === 0 ? (
            <p className="text-sm text-[#8a8a8a] py-3">本日休息，請選擇其他日期。</p>
          ) : (
            <div className="flex flex-wrap gap-2.5">
              {TIME_SLOTS.filter((s) => availableSlots.includes(s.value)).map((slot) => (
                <button
                  key={slot.value}
                  type="button"
                  onClick={() => onTimeChange(slot.value)}
                  className={`
                    px-5 py-2.5 rounded-full text-sm font-medium border transition-all cursor-pointer whitespace-nowrap
                    ${selectedTime === slot.value
                      ? 'bg-[#b8956a] text-white border-[#b8956a]'
                      : 'bg-white text-[#5a5a5a] border-[#e2ddd8] hover:border-[#b8956a] hover:text-[#b8956a]'
                    }
                  `}
                >
                  {slot.label}
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-[#8a8a8a] mt-3">
            週一至週五 10:00–19:00・週六 10:00–16:00（預約制）・週日休息
          </p>
        </div>
      )}

      {/* Hidden inputs for form submission */}
      <input type="hidden" name="appointment_date" value={selectedDate} />
      <input type="hidden" name="appointment_time" value={selectedTime} />
    </div>
  );
}
